import { ReactNode } from 'react';

import { Icon, IconNames, Shimmer, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';

interface BillingContentRowProps {
  readonly loading?: boolean;
  readonly icon?: IconNames;
  readonly label: string;
  readonly children?: ReactNode;
  readonly className?: string;
}

interface BillingContentRowsProps {
  readonly loading?: boolean;
  readonly rows?: BillingContentRowProps[];
  readonly children?: ReactNode;
  readonly className?: string;
}

interface BillingContentProps {
  readonly title?: string;
  readonly children?: ReactNode;
  readonly className?: string;
}

function BillingContentRow({ loading, icon, label, children, className }: BillingContentRowProps) {
  return (
    <div className={twMerge('flex flex-row gap-4 py-5 text-neutral-700', className)}>
      {icon ? <Icon icon={icon} /> : null}
      {loading ? (
        <div className="flex flex-1">
          <Shimmer size="md" />
        </div>
      ) : (
        <Text variant="subtitle-2" weight="medium" className="flex flex-1">
          {label}
        </Text>
      )}
      {/* eslint-disable-next-line no-nested-ternary */}
      {loading ? (
        <Shimmer size="md" className="w-32" />
      ) : typeof children === 'string' ? (
        <Text variant="subtitle-2">{children}</Text>
      ) : (
        children
      )}
    </div>
  );
}

function BillingContentRows({ loading, rows, className, children }: BillingContentRowsProps) {
  return (
    <div className={twMerge('rounded-xl bg-neutral-50 px-8', className)}>
      {rows?.map((row, index) => <BillingContentRow loading={loading} key={index} {...row} />)}
      {children}
    </div>
  );
}

function BillingContent({ title, children, className }: BillingContentProps) {
  return (
    <div className={twMerge('flex flex-col gap-4', className)}>
      {title ? (
        <Text as="h4" variant="subtitle-2" weight="semibold" color="black" className="p-4">
          {title}
        </Text>
      ) : null}
      {children}
    </div>
  );
}

BillingContent.Rows = BillingContentRows;
BillingContent.Row = BillingContentRow;

export { BillingContent };
